<script lang="ts">
	import { base } from '$app/paths';
	import { page } from '$app/stores';
	import { onMount, tick } from 'svelte';
	import Footer from './Footer.svelte';
	import LoginOverlay from './LoginOverlay.svelte';
	import Logo from './Logo.svelte';
	import { clickOutside } from './actions/clickOutside';
	import { filterIsVisible } from './shared/filter';
	import { isAuthenticated, user } from './stores/auth';
	import { activeItem, height, loginOverlayIsVisible } from './stores/header';
	import { campaignsExpanded, footerIsVisible, nav, navWasIsVisibleOnce } from './stores/nav';
	import { notifications } from './stores/notifications';
	import { submissionFormIsVisible } from './stores/submission';
	import { height as heightTransition } from './transitions/height';

	export let project: Project;
	export let projects: Project[];

	let closeButton;
	let navButton;

	$: {
		if ($navWasIsVisibleOnce) {
			if ($nav) {
				tick().then(() => {
					closeButton?.focus();
				});
			} else {
				tick().then(() => {
					navButton?.focus();
				});
			}
		}
	}

	let header;
	$: header && header.toggleAttribute('inert', $nav);

	$: if ($page.url.pathname) {
		$loginOverlayIsVisible = false;
	}

	onMount(() => {
		if (document.location.hash === '#register') {
			document.location.hash = '';
			toggleLoginOverlay('Registrieren');
		}
	});

	const toggleLoginOverlay = (tab: 'Login' | 'Registrieren' = 'Login') => {
		$activeItem = tab;
		$loginOverlayIsVisible = !$loginOverlayIsVisible;
	};

	let campaignButton: HTMLButtonElement;

	// Campaign without url is considered the current campaign.
	const campaigns: Campaign[] = [
		{ title: project.title },
		...projects
			.filter((p) => p.name !== project.name)
			.map((p) => ({
				title: p.title,
				url: `${p.host}/${p.slug}`,
			})),
	];

	let showCampaignSwitch = false;
	let showCampaignSwitchArrow = false;
	let campaignSwitchTitle = '';
	const startCampaignSwitchAnimation = () => {
		showCampaignSwitch = true;
		let index = 0;

		const interval = setInterval(() => {
			campaignSwitchTitle = campaigns[0].title.slice(0, index);
			index++;
			if (index === campaigns[0].title.length + 1) {
				clearInterval(interval);
				setTimeout(() => {
					showCampaignSwitchArrow = true;
				}, 500);
			}
		}, 40);
	};
</script>

<header
	class="z-20 leading-none sticky top-0 bg-opacity-smidge backdrop-filter backdrop-blur-sm {$footerIsVisible
		? 'bg-white'
		: 'bg-beige'}"
	class:border-b={$page.url.pathname !== '/briefe' || !$filterIsVisible}
	class:hidden={$submissionFormIsVisible}
	bind:clientHeight={$height}
	bind:this={header}
>
	<div
		class="py-5 px-5 md:px-6 justify-between items-center grid grid-cols-2 md:grid-cols-[1fr_auto_1fr] gap-5"
		class:invisible={$loginOverlayIsVisible}
	>
		<span class="flex whitespace-nowrap">
			<Logo on:animationend={startCampaignSwitchAnimation} />
			{#if showCampaignSwitch}
				<button
					bind:this={campaignButton}
					type="button"
					class="text-base ml-1 inline-flex items-center"
					aria-pressed={$campaignsExpanded}
					on:click={() => {
						$campaignsExpanded = !$campaignsExpanded;
					}}
					><span>{campaignSwitchTitle}</span>
					{#if showCampaignSwitchArrow}
						<svg
							viewBox="0 0 20 10"
							width="10"
							height="5"
							class="ml-1 transition-transform"
							class:-scale-y-100={$campaignsExpanded}
						>
							<path
								stroke="currentColor"
								d="M1 1l9 8l9-8"
								stroke-width="1"
								vector-effect="non-scaling-stroke"
								fill="none"
							/>
						</svg>
					{/if}
				</button>
			{/if}
		</span>

		<div class="text-center text-base hidden md:block">
			{#if $page.url.pathname !== '/'}Wienbibliothek im Rathaus{/if}
		</div>

		<div class="text-right justify-end flex items-center space-x-4">
			<div class="hidden sm:flex space-x-18px items-center">
				{#if $isAuthenticated}
					{#if $user.lockedObjects.length}
						<a
							href="{base}/meine-beitraege/#meine-briefe"
							class="flex items-center text-sm uppercase text-orange"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 10 10"
								width="10"
								height="10"
							>
								<circle cx="5" cy="5" r="5" stroke="none" fill="currentColor" />
							</svg>
							<div class="ml-2 hidden lg:block">
								{$user.lockedObjects.length}
								{$user.lockedObjects.length === 1 ? 'Brief' : 'Briefe'} in Bearbeitung
							</div>
						</a>
					{/if}
					<a
						class="button-secondary inline-flex items-center space-x-2"
						href="{base}/meine-beitraege/{$notifications?.total
							? '#benachrichtigungen'
							: ''}"
					>
						{#if $notifications?.notifications.some((n) => n.status === 'UNREAD')}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 10 10"
								width="10"
								height="10"
								class="text-blue"
							>
								<circle cx="5" cy="5" r="5" stroke="none" fill="currentColor" />
							</svg>
						{/if}
						<span>{$user.name}</span>
					</a>
				{:else}
					<button
						class="button-secondary"
						type="button"
						on:click={() => toggleLoginOverlay()}>Login</button
					>
				{/if}

				<div>
					<button
						type="button"
						aria-pressed={$nav}
						on:click={nav.show}
						bind:this={navButton}
						class="p-2 -m-2"
					>
						<span class="sr-only">Menü</span>
						<svg
							viewBox="0 0 26 24"
							width="26"
							height="24"
							aria-hidden="true"
							focusable="false"
						>
							<path d="M0 12H26 M0 4H26 M0 20H26" stroke="currentColor" />
						</svg>
					</button>
				</div>
			</div>
		</div>
	</div>

	{#if $campaignsExpanded}
		<div
			transition:heightTransition={{}}
			use:clickOutside={{
				callback: () => {
					$campaignsExpanded = false;
				},
				ignore: [campaignButton],
			}}
		>
			<div
				class="px-5 md:px-6 text-3xl md:text-6xl lg:text-7xl leading-[1.083333] pt-5 pb-10"
			>
				<ul>
					{#each campaigns as campaign}
						<li>
							{#if campaign.url}
								<a
									href={campaign.url}
									target="_blank"
									rel="noreferrer"
									class="no-underline text-gray-c">{campaign.title}</a
								>
							{:else}
								<a
									href="{base}/"
									class="no-underline"
									on:click={() => {
										$campaignsExpanded = false;
									}}>{campaigns[0].title}</a
								>
							{/if}
						</li>
					{/each}
					<li>
						<a
							href="https://crowdsourcing.wien/#archiv"
							target="_blank"
							rel="noreferrer"
							class="no-underline text-gray-c">Archiv</a
						>
					</li>
				</ul>
			</div>
		</div>
	{/if}

	<div
		id="header-meta"
		class="text-lg-fluid px-5 md:px-6"
		class:opacity-20={$campaignsExpanded}
	/>
</header>

<div
	id="header-expand"
	style="top: {$height}px; height:{$filterIsVisible ? `calc(100vh - ${$height}px)` : 'auto'}"
	class={$filterIsVisible
		? 'fixed z-50 top-0 left-0 w-full flex flex-col bg-white bg-opacity-smidge backdrop-filter backdrop-blur-sm overflow-auto overscroll-contain px-5 md:px-6 pt-4'
		: ''}
/>

{#if $loginOverlayIsVisible}
	<LoginOverlay on:close={() => toggleLoginOverlay()} />
{/if}

{#if $nav}
	<article
		class="fixed z-50 top-0 left-0 w-full h-full flex flex-col bg-white bg-opacity-smidge backdrop-filter backdrop-blur-sm overflow-auto overscroll-contain"
	>
		<div class="pt-6 pb-7 px-5 md:px-6 flex items-start justify-between">
			<button
				type="button"
				class="order-last p-3 -m-3"
				on:click={nav.hide}
				bind:this={closeButton}
			>
				<span class="sr-only">Menü schließen</span>
				<svg
					viewBox="0 0 18 18"
					width="18"
					height="18"
					aria-hidden="true"
					focusable="false"
				>
					<path
						d="M1 1L17 17 M17 1 L1 17"
						stroke="currentColor"
						stroke-linecap="square"
					/>
				</svg>
			</button>
			<div class="text-xl-fluid">
				<nav class="nav">
					<ul class="text-gray-45">
						<li class:text-black={$page.url.pathname === base + '/'}>
							<a
								href="{base}/"
								aria-current={$page.url.pathname === base + '/'
									? 'page'
									: undefined}>Information</a
							>
						</li>

						<li class:text-black={$page.url.pathname === base + '/briefe/'}>
							<a
								href="{base}/briefe/"
								aria-current={$page.url.pathname === base + '/briefe/'
									? 'page'
									: undefined}>Briefe</a
							>
						</li>
						<li
							class:text-black={$page.url.pathname ===
								base + '/transkriptionsregeln/'}
						>
							<a
								href="{base}/transkriptionsregeln/"
								aria-current={$page.url.pathname === base + '/transkriptionsregeln/'
									? 'page'
									: undefined}>Transkriptionsregeln</a
							>
						</li>

						<li class:text-black={$page.url.pathname === base + '/beitraeger-innen/'}>
							<a
								href="{base}/beitraeger-innen/"
								aria-current={$page.url.pathname === base + '/beitraeger-innen/'
									? 'page'
									: undefined}>Beiträger*innen</a
							>
						</li>
						<li class:text-black={$page.url.pathname === base + '/faq/'}>
							<a
								href="{base}/faq/"
								aria-current={$page.url.pathname === base + '/faq/'
									? 'page'
									: undefined}>FAQ</a
							>
						</li>
						<li
							class:text-black={$page.url.pathname === base + '/meine-beitraege/'}
							class="mt-16"
						>
							{#if $isAuthenticated}
								<a
									href="{base}/meine-beitraege/"
									aria-current={$page.url.pathname === base + '/meine-beitraege/'
										? 'page'
										: undefined}>Meine Beiträge</a
								>
							{:else}
								<button
									type="button"
									on:click={() => {
										nav.hide();
										toggleLoginOverlay();
									}}>Login</button
								>
							{/if}
						</li>
					</ul>
				</nav>
			</div>
		</div>
		<div class="mt-auto">
			<Footer nested={true} />
		</div>
	</article>
{/if}

<style lang="postcss">
	#header-meta {
		&:not(:empty) {
			padding-block-end: 1rem;
		}
	}
	.nav a:hover {
		text-decoration: none;
	}

	:global(#header-expand > div) {
		height: 100%;
	}
</style>
